import { Inject, Injectable, LOCALE_ID, OnDestroy } from '@angular/core';
import { ApmService } from '@elastic/apm-rum-angular';
import {
  Action,
  Selector,
  SelectorOptions,
  State,
  StateContext,
  Store,
} from '@ngxs/store';
import produce from 'immer';
import { combineLatest, defer, from, iif, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { ApiService, NodeApiService, totalFee } from '@pe/checkout/api';
import type { EncryptionService } from '@pe/checkout/encryption';
import { PaymentOptionDataInterface } from '@pe/checkout/payment';
import { ProductsStateService } from '@pe/checkout/products';
import { FlowStorage } from '@pe/checkout/storage';
import {
  AddressTypeEnum,
  BusinessType,
  CartItemExInterface,
  FlowCloneReason,
  FlowInterface,
  NodePaymentCartItemInterface,
  NodePaymentInterface,
  NodePaymentResponseInterface,
  PaymentMethodEnum,
  PaymentOptionConnectionInterface,
  PaymentOptionInterface,
  PollingConfig,
  REMEMBER_ME_KEY,
  ResponseErrorsInterface,
  SalutationEnum,
  SectionType,
} from '@pe/checkout/types';
import { extractStreetNameAndNumber } from '@pe/checkout/utils/address';
import { POLLING_CONFIG, pollWhile } from '@pe/checkout/utils/poll';
import { EnvironmentConfigInterface, PE_ENV } from '@pe/common';

import { AuthSelectors, Register } from '../auth';
import { CheckoutState, HidePayment, SetPaymentComplete } from '../checkout';
import { CloneFlow, FlowState, SetLastActiveConnectionId } from '../flow';
import { SettingsState } from '../settings';
import { OpenStep } from '../steps';

import { PaymentProxyService } from './payment-proxy.service';
import {
  EditPayment,
  GetApiCallData,
  GetPaymentOptions,
  PatchPaymentDetails,
  PatchPaymentResponse,
  GetPayment,
  PostPayment,
  SetPaymentDetails,
  SetPaymentOptions,
  SetPayments,
  UpdatePayment,
  PollPaymentForStatus,
  PollUpdatePayment,
  ChangeFailedPayment,
  SetFormState,
  PatchFormState,
  ClearFormState,
  SetPaymentError,
  ClearPaymentState,
} from './payment.actions';


type PaymentDetails = any;

interface PaymentData<T = any> {
  details: PaymentDetails;
  form: T;
  formOptions: any;
  error: ResponseErrorsInterface;
  response: NodePaymentResponseInterface<any>;
}

export type PaymentStateModel = {
  [Key in PaymentMethodEnum]: {
    [key: string]: PaymentData
  };
}

export const initialPaymentState = Object.values(PaymentMethodEnum).reduce(
  (acc, curr) => {
    acc[curr] = {};

    return acc;
  },
  {} as PaymentStateModel
);

@State<PaymentStateModel>({
  name: 'payment',
  defaults: initialPaymentState,
})
@SelectorOptions({
  injectContainerState: false,
  suppressErrors: false,
})
@Injectable()
export class PaymentState implements OnDestroy {

  private readonly destroy$ = new Subject<boolean>();

  constructor(
    @Inject(POLLING_CONFIG) private readonly pollingConfig: PollingConfig,
    @Inject(PE_ENV) private readonly env: EnvironmentConfigInterface,
    @Inject(LOCALE_ID) private readonly locale: string,
    private readonly store: Store,
    private readonly apiService: ApiService,
    private readonly nodeApiService: NodeApiService,
    private readonly paymentProxyService: PaymentProxyService,
    private readonly productsStateService: ProductsStateService,
    private readonly flowStorage: FlowStorage,
    private readonly apmService: ApmService,
  ) { }

  @Selector([
    PaymentState,
    FlowState.paymentMethod,
    FlowState.flow,
  ]) private static data(
    state: PaymentStateModel,
    paymentMethod: PaymentMethodEnum,
    flow: FlowInterface,
  ) {
    return state[paymentMethod]?.[flow.connectionId] ?? {};
  }

  @Selector([
    PaymentState,
  ]) static responseByPaymentMethod(
    state: PaymentStateModel,
  ) {
    return (paymentMethod: PaymentMethodEnum) =>
      Object.values(state[paymentMethod] ?? {}).find(r => r.response)?.response;
  }

  @Selector([PaymentState.data]) static details(data: PaymentData) {
    return data.details;
  }

  @Selector([PaymentState.data]) static options(data: PaymentData) {
    return data.formOptions;
  }

  @Selector([PaymentState.data]) static response<T = any>(
    data: PaymentData,
  ): NodePaymentResponseInterface<T> {
    return data.response;
  }

  @Selector([PaymentState.data]) static form(data: PaymentData) {
    return data.form;
  }

  @Selector([PaymentState.data]) static error(data: PaymentData) {
    return data.error;
  }

  @Selector([
    FlowState.flow,
    PaymentState.details,
    FlowState.paymentOption,
    FlowState.activeConnection,
  ]) static paymentPayload(
    flow: FlowInterface,
    paymentDetails: PaymentDetails,
    paymentOption: PaymentOptionInterface,
    activeConnection: PaymentOptionConnectionInterface,
  ): NodePaymentInterface<any> {
    const companyData = (
      flow.businessType
      && flow.businessType !== BusinessType.B2C
      && (flow?.company?.externalId || flow?.company?.name)
    )
      ? { company: flow.company }
      : {};

    const splitPaymentData = flow?.splits?.find(split => !split.isPaid && split.connectionId === flow.connectionId);
    const paymentFee = totalFee(paymentOption, flow);
    const splitAmount = activeConnection.merchantCoversFee
      ? splitPaymentData?.amount?.value
      : Math.max(0, splitPaymentData?.amount?.value - paymentFee);

    return {
      paymentDetails: {
        ...paymentDetails,
        ...flow.extra?.forOwnAccount ? { forOwnAccount: flow.extra.forOwnAccount } : {},
        ...flow.extra?.dataPrivacy ? { dataPrivacy: flow.extra.dataPrivacy } : {},
        ...flow.extra?.schufaAcceptance
          ? { schufaAcceptance: flow.extra.schufaAcceptance }
          : {},
      },
      ...flow?.metadata && { metadata: flow.metadata },
      payment: {
        flowId: flow.id,
        reference: flow.reference,
        ...flow.referenceExtra && { referenceExtra: flow.referenceExtra },
        total: splitAmount && !Number.isNaN(splitAmount) ? splitAmount : flow.total,
        amount: splitAmount && !Number.isNaN(splitAmount) ? splitAmount : flow.amount,
        currency: splitPaymentData?.amount?.currency ?? flow.currency,
        customerName: flow.billingAddress ? `${flow.billingAddress?.firstName} ${flow.billingAddress?.lastName}`.trim() : null,
        customerEmail: flow.billingAddress?.email,
        businessId: flow.businessId,
        businessName: flow.businessName,
        deliveryFee: flow.deliveryFee || 0,
        shippingOrderId: flow.shippingOrderId,
        shippingMethodName: flow.shippingMethodName,
        apiCallId: flow.apiCall?.id,
        channel: flow.channel,
        channelSetId: flow.channelSetId,
        channelSource: flow.channelSource,
        channelType: flow.channelType,
        customerType: flow.customerType,
        ...(flow.apiCall?.company
          && typeof flow.apiCall?.company === 'object'
          && flow.apiCall?.company?.externalId ? { company: flow.apiCall.company }
          : companyData),
        ...flow.billingAddress && { address: this.getAddress(flow, AddressTypeEnum.BILLING) },
        ...flow.shippingAddress && { shippingAddress: this.getAddress(flow, AddressTypeEnum.SHIPPING) },
        ...paymentDetails?.downPayment && { downPayment: paymentDetails.downPayment },
        ...flow.shippingOption || flow.apiCall?.shippingOption
          ? { shippingOption: flow.shippingOption || flow.apiCall.shippingOption }
          : {},
        ...flow.order && { orderId: flow.order.id },
        ...flow.splits?.length && { splits: flow.splits },
      },
      paymentItems: (flow.cart || []).map(item => ({
        identifier: item.identifier,
        name: item.name,
        price: item.price,
        priceNet: item.priceNet,
        vatRate: item.vat,
        thumbnail: item.image,
        quantity: item.quantity,
        sku: item.sku,
        productId: item.productId || item._id || item.id,
        options: item.options,
        orderReference: item.orderReference,
        extraData: {
          subscriptionPlan: null,
        },
      })),
      ...paymentDetails?.forceRedirect && { forceRedirect: paymentDetails.forceRedirect },
      ...flow.apiCall?.skipHandlePaymentFee && { skipHandlePaymentFee: flow.apiCall.skipHandlePaymentFee },
      ...flow.apiCall?.subscription && { subscription: flow.apiCall.subscription },
      ...flow.apiCall?.subscriptionExecutionId && { subscriptionExecutionId: flow.apiCall.subscriptionExecutionId },
    };
  }

  private static getAddress(flow: FlowInterface, addressType: AddressTypeEnum) {
    const key = `${addressType}Address` as keyof FlowInterface;
    const {
      city,
      country,
      email,
      firstName,
      lastName,
      phone,
      birthDate,
      salutation,
      street,
      streetName,
      streetNumber,
      zipCode,
    } = flow[key];

    const [extractedStreetName, extractedStreetNumber] = extractStreetNameAndNumber(street);

    const address = {
      city,
      country,
      email,
      firstName,
      lastName,
      phone,
      birthDate,
      street,
      zipCode,
      salutation: salutation === SalutationEnum.SALUTATION_OTHER ? '' : salutation,
      streetName: streetName || extractedStreetName,
      streetNumber: streetNumber || extractedStreetNumber,
    };

    return Object.values(address).some(Boolean) ? address : undefined;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @Action(SetPayments)
  setPayments({ getState, setState }: StateContext<PaymentStateModel>, action: SetPayments) {
    const payments = getState();

    setState({
      ...initialPaymentState,
      ...payments,
      ...action.payload,
    });
  }

  @Action(SetPaymentDetails)
  setPayment(
    { getState, patchState }: StateContext<PaymentStateModel>,
    action: SetPaymentDetails,
  ) {
    const payments = getState();
    const paymentMethod = this.store.selectSnapshot(FlowState.paymentMethod);
    const { connectionId } = this.store.selectSnapshot(FlowState.flow);

    const state = produce(payments[paymentMethod], (draft) => {
      draft[connectionId] = {
        ...draft[connectionId],
        details: action.payload,
      };
    });

    patchState({ [paymentMethod]: state });
  }

  @Action(PatchPaymentDetails)
  patchPayment(
    { getState, patchState }: StateContext<PaymentStateModel>,
    action: PatchPaymentDetails,
  ) {
    const payments = getState();
    const paymentMethod = this.store.selectSnapshot(FlowState.paymentMethod);
    const { connectionId } = this.store.selectSnapshot(FlowState.flow);
    const payload = this.appendGuestTokenToRedirectUrls(action.payload);

    const state = produce(
      payments[paymentMethod],
      (draft) => {
        draft[connectionId] = {
          ...draft[connectionId],
          details: {
            ...draft[connectionId]?.details,
            ...payload,
          },
        } as PaymentData;
      },
    );

    patchState({ [paymentMethod]: state });
  }

  @Action(PatchPaymentResponse)
  patchResponse(
    { getState, patchState }: StateContext<PaymentStateModel>,
    action: PatchPaymentResponse,
  ) {
    const paymentMethod = this.store.selectSnapshot(FlowState.paymentMethod);
    const { connectionId } = this.store.selectSnapshot(FlowState.flow);
    const { [paymentMethod]: payment } = getState();

    const state = produce(
      payment,
      (draft) => {
        draft[connectionId] = {
          ...draft[connectionId],
          response: action.payload,
        };
      },
    );

    patchState({ [paymentMethod]: state });
  }

  @Action(GetPaymentOptions, { cancelUncompleted: true })
  getPaymentOptions(
    { dispatch }: StateContext<PaymentStateModel>,
    { paymentOptionData, doNotStoreFormOptions }: GetPaymentOptions
  ) {
    const flow = this.store.selectSnapshot(FlowState.flow);

    return this.apiService.getFormOptions(
      flow.id,
      paymentOptionData?.connectionId ?? flow.connectionId,
    ).pipe(
      switchMap(options => dispatch(new SetPaymentOptions(options, paymentOptionData, doNotStoreFormOptions))),
    );
  }

  @Action(SetPaymentOptions)
  setPaymentOptions(
    { getState, patchState }: StateContext<PaymentStateModel>,
    { payload, paymentOptionData, doNotStoreFormOptions }: SetPaymentOptions,
  ) {
    const { paymentMethod, connectionId, payment } = this.preparePaymentStateData(getState(), paymentOptionData);

    try {
      const state = produce(payment, (draft) => {
        draft[connectionId] = {
          ...draft?.[connectionId],
          formOptions: doNotStoreFormOptions ? {} : payload,
        };
      });

      patchState({ [paymentMethod]: state });
    } catch {
      if (!payment?.[connectionId]) {
        const payments = this.store.selectSnapshot(PaymentState);
        const flow = this.store.selectSnapshot(FlowState);
        this.apmService.apm.setCustomContext({
          payments,
          flow,
        });
        this.apmService.apm.captureError('SetPaymentOptions error');
      }
    }
  }

  @Action(GetApiCallData)
  getApiCallData(
    { dispatch }: StateContext<PaymentStateModel>,
    action: GetApiCallData,
  ) {
    const flowId = this.store.selectSnapshot(FlowState.flowId);

    return this.apiService.getApiCallData(flowId).pipe(
      switchMap((apiCall) => {
        const state = produce(
          action.payload,
          (draft) => {
            draft._apiCall = apiCall;
          },
        );

        return dispatch(new PatchPaymentResponse(state));
      }),
    );
  }

  @Action(PostPayment, { cancelUncompleted: true })
  postPayment(
    { dispatch }: StateContext<PaymentStateModel>,
  ) {
    const flow = this.store.selectSnapshot(FlowState.flow);
    const payload = this.store.selectSnapshot(PaymentState.paymentPayload);

    return combineLatest([
      this.loadEncryptionModule(),
      this.prepareNodePaymentData<unknown>(payload).pipe(
        switchMap((data) => {
          const remembeMe = this.flowStorage.getData<boolean>(flow.id, REMEMBER_ME_KEY);
          const flowId = this.store.selectSnapshot(FlowState.flowId);

          if (remembeMe) {
            return dispatch(new Register(
              flowId,
              {
                country: data.payment?.address?.country.toLowerCase(),
                email: data.payment.customerEmail,
                first_name: data.payment.address?.firstName,
                last_name: data.payment.address?.lastName,
                forceGeneratePassword: true,
              },
              {
                registrationOrigin: {
                  url: window.location.href,
                  account: 'personal',
                },
              },
            )).pipe(
              map(() => data),
            );
          }

          return of(data);
        }),
      ),
    ]).pipe(
      switchMap(([encryptionService, data]) =>
        from(encryptionService.encryptWithSalt(data, data.payment.businessId)).pipe(
          switchMap(encryptedData => this.nodeApiService.submitPayment<unknown>(
            flow.connectionId,
            encryptedData,
          )),
        ),
      ),
      switchMap(response => response.payment.apiCallId
        ? dispatch(new GetApiCallData(response))
        : dispatch(new PatchPaymentResponse(response))
      ),
      catchError((err) => {
        this.store.dispatch([new SetPaymentError(err), new SetPaymentComplete()]);

        return throwError(err);
      })
    );
  }

  @Action(EditPayment, { cancelUncompleted: true })
  editPayment(
    { dispatch }: StateContext<PaymentStateModel>,
    action: EditPayment,
  ) {
    const flow = this.store.selectSnapshot(FlowState.flow);
    const paymentMethod = this.store.selectSnapshot(FlowState.paymentMethod);
    const { forceRedirect, ...payload } = this.store.selectSnapshot(PaymentState.paymentPayload);

    return this.prepareNodePaymentData(payload).pipe(
      switchMap(data => this.nodeApiService.editTransaction(
        paymentMethod,
        flow.businessId,
        action.payload,
        flow.id,
        data,
      )),
      catchError(err => dispatch([
        new SetPaymentError(err),
        new SetPaymentComplete(),
      ]).pipe(
        switchMap(() => throwError(err)),
      )),
      switchMap(() => dispatch(new UpdatePayment(action.payload))),
    );
  }

  @Action(UpdatePayment, { cancelUncompleted: true })
  updatePayment({ dispatch }: StateContext<PaymentStateModel>, action: UpdatePayment) {
    const flow = this.store.selectSnapshot(FlowState.flow);

    return this.nodeApiService.updatePayment(
      flow.connectionId,
      action.payload,
    ).pipe(
      switchMap(response => response.payment.apiCallId
        ? dispatch(new GetApiCallData(response))
        : dispatch(new PatchPaymentResponse(response))
      ),
    );
  }

  @Action(PollUpdatePayment, { cancelUncompleted: true })
  pollUpdatePayment(
    { getState, dispatch }: StateContext<PaymentStateModel>,
    action: PollUpdatePayment,
  ) {
    const paymentMethod = this.store.selectSnapshot(FlowState.paymentMethod);
    const { connectionId } = this.store.selectSnapshot(FlowState.flow);
    const { [paymentMethod]: payment } = getState();

    return this.nodeApiService.updatePayment(
      connectionId,
      payment[connectionId].response.id,
    ).pipe(
      pollWhile(
        this.pollingConfig,
        () => {
          const { [paymentMethod]: payment } = getState();

          return !action.payload.includes(payment[connectionId].response.payment.status);
        },
        false,
      ),
      switchMap(response => response.payment.apiCallId
        ? dispatch(new GetApiCallData(response))
        : dispatch(new PatchPaymentResponse(response))
      ),
    );
  }

  @Action(GetPayment, { cancelUncompleted: true })
  getPayment({ dispatch, getState }: StateContext<PaymentStateModel>) {
    const paymentMethod = this.store.selectSnapshot(FlowState.paymentMethod);
    const { connectionId } = this.store.selectSnapshot(FlowState.flow);
    const { [paymentMethod]: payment } = getState();

    return this.nodeApiService.requestPayment(connectionId, payment[connectionId].response.id).pipe(
      switchMap(response => dispatch(new PatchPaymentResponse(response))),
    );
  }

  @Action(PollPaymentForStatus, { cancelUncompleted: true })
  pollPaymentForStatus(
    { dispatch, getState }: StateContext<PaymentStateModel>,
    action: PollPaymentForStatus,
  ) {
    const paymentMethod = this.store.selectSnapshot(FlowState.paymentMethod);
    const { connectionId } = this.store.selectSnapshot(FlowState.flow);
    const { [paymentMethod]: payment } = getState();

    return this.nodeApiService.requestPayment(connectionId, payment[connectionId].response.id).pipe(
      pollWhile(
        this.pollingConfig,
        () => {
          const { [paymentMethod]: payment } = getState();

          return !action.payload.includes(payment[connectionId].response.payment.status);
        },
        false,
      ),
      switchMap(response => response.payment.apiCallId
        ? dispatch(new GetApiCallData(response))
        : dispatch(new PatchPaymentResponse(response))
      ),
    );
  }

  @Action(ChangeFailedPayment)
  changeFailedPayment(
    { dispatch }: StateContext<PaymentStateModel>,
    { payload }: ChangeFailedPayment,
  ) {
    const paymentMethod = this.store.selectSnapshot(FlowState.paymentOption);
    const activeConnection = this.store.selectSnapshot(FlowState.activeConnection);
    const paymentOptions = this.store.selectSnapshot(CheckoutState.paymentOptions);
    const flow = this.store.selectSnapshot(FlowState.flow);

    return iif(
      () => paymentOptions.length < 1,
      new Observable((sub) => {
        window.location.href = payload.redirectUrl
          || flow.apiCall.cancelUrl
          || flow.apiCall.failureUrl
          || `${this.env.frontend.checkoutWrapper}/${this.locale}/pay/static-finish/fail`;


        sub.next();
        sub.complete();
      }),
      dispatch([
        new SetPaymentError(null),
        ...(!flow.disableValidation && (payload.hidePayment === undefined || payload.hidePayment === true)) ? [new HidePayment({
          paymentMethod: paymentMethod.paymentMethod,
          paymentIssuer: paymentMethod.paymentIssuer,
        })] : [],
        new SetLastActiveConnectionId({ connectionId: activeConnection.id }),
        new CloneFlow({
          reason: FlowCloneReason.ChangePaymentOnFail,
          skipData: false,
          redirect: true,
          init: true,
        }),
      ]).pipe(
        switchMap(() => {
          const paymentOptions = this.store.selectSnapshot(CheckoutState.paymentOptions);

          return iif(
            () => paymentOptions.length < 1,
            new Observable((sub) => {
              dispatch(new SetPaymentComplete(false));

              window.location.href = payload.redirectUrl
                || flow.apiCall.cancelUrl
                || flow.apiCall.failureUrl
                || `${this.env.frontend.checkoutWrapper}/${this.locale}/pay/static-finish/fail`;

              sub.next();
              sub.complete();
            }),
            defer(() => dispatch([
              new SetPaymentComplete(false),
              new OpenStep(SectionType.ChoosePayment),
            ])),
          );
        }),
      )
    );
  }

  @Action(SetFormState)
  setFormState<T extends { 'pe-form-token': string }>(
    { getState, patchState }: StateContext<PaymentStateModel>,
    { form, paymentOptionData }: SetFormState<T>,
  ) {
    const { paymentMethod, connectionId, payment } = this.preparePaymentStateData(getState(), paymentOptionData);


    delete form['pe-form-token'];

    const state = produce(payment, (draft) => {
      draft[connectionId] = {
        ...draft[connectionId],
        form,
      };
    });

    patchState({ [paymentMethod]: state });
  }

  @Action(PatchFormState)
  patchFormState<T extends { 'pe-form-token': string }>(
    { getState, patchState }: StateContext<PaymentStateModel>,
    { form, paymentOptionData }: PatchFormState<T>,
  ) {
    const { paymentMethod, connectionId, payment } = this.preparePaymentStateData(getState(), paymentOptionData);
    delete form['pe-form-token'];

    const state = produce(payment, (draft) => {
      draft[connectionId] = {
        ...draft[connectionId],
        form: {
          ...draft[connectionId]?.form,
          ...form,
        },
      } as PaymentData;
    });

    patchState({ [paymentMethod]: state });
  }

  @Action(ClearFormState)
  clearFormState({ getState, patchState }: StateContext<PaymentStateModel>) {
    const paymentMethod = this.store.selectSnapshot(FlowState.paymentMethod);
    const { connectionId } = this.store.selectSnapshot(FlowState.flow);
    const { [paymentMethod]: payment } = getState();

    const state = produce(payment, (draft) => {
      draft[connectionId] = {
        ...draft[connectionId],
        form: null,
      };
    });

    patchState({ [paymentMethod]: state });
  }

  @Action(SetPaymentError)
  paymentError(
    { getState, patchState }: StateContext<PaymentStateModel>,
    { error }: SetPaymentError) {
    const paymentMethod = this.store.selectSnapshot(FlowState.paymentMethod);
    const { connectionId } = this.store.selectSnapshot(FlowState.flow);
    const { [paymentMethod]: payment } = getState();

    const state = produce(payment, (draft) => {
      draft[connectionId] = {
        ...draft[connectionId],
        error,
      };
    });

    patchState({ [paymentMethod]: state });
  }

  @Action(ClearPaymentState)
  clearState({ patchState, getState }: StateContext<PaymentStateModel>) {
    const paymentMethod = this.store.selectSnapshot(FlowState.paymentMethod);
    const { connectionId } = this.store.selectSnapshot(FlowState.flow);
    const { [paymentMethod]: payment } = getState();

    const state = produce(payment, (draft) => {
      draft[connectionId] = {} as PaymentData;
    });

    patchState({ [paymentMethod]: state });
  }

  private loadEncryptionModule(): Observable<EncryptionService> {
    return from(
      import('@pe/checkout/encryption')
        .then(({ EncryptionService }) => new EncryptionService()),
    );
  }

  private prepareNodePaymentData<PaymentDetails>(
    data: NodePaymentInterface<PaymentDetails>,
  ): Observable<NodePaymentInterface<PaymentDetails>> {

    const settings = this.store.selectSnapshot(SettingsState.settings);

    const productIds = data.paymentItems.reduce((acc, curr) => {
      if (curr.productId) {
        acc.push(curr.productId);
      }

      return acc;
    }, []);

    let req$ = of(data);

    if (productIds.length) {
      req$ = this.productsStateService.getProductsOnce(productIds, settings.businessUuid).pipe(
        map((productsFull) => {
          const fullCartItems = this.productsStateService.cartItemsToExtended(
            productsFull,
            data.paymentItems.map(p => ({ id: p.productId, productId: p.productId, options: p.options })),
            settings,
          );

          data.paymentItems.forEach((item) => {
            // When product doesn't exists on product server (come directly from shop)
            //  it can have uuid and id as null
            const full = fullCartItems.find(c => c.productId
              || c.id
              || c.identifier
              && c.productId === item.productId
              || c.id === item.productId
              || c.identifier === item.identifier
            );

            // Sometimes product can be non-existing on product server (come directly from shop)
            if (full && !Object.values(full).every(f => f === undefined)) {
              this.cartItemToNodeItem(full, item);
            }

            const productFull = productsFull.find(c => c.uuid === item.productId || c.id === item.productId);
            if (productFull) {
              item.description = productFull.description;
            }
          });

          return data;
        }),
      );
    }

    return req$;
  }

  private cartItemToNodeItem(
    cartItem: CartItemExInterface,
    nodeItem: NodePaymentCartItemInterface,
  ): void {
    if (cartItem && nodeItem) {
      nodeItem.identifier = cartItem.identifier;
      nodeItem.name = cartItem.name;
      nodeItem.price = cartItem.price;
      nodeItem.sku = cartItem.sku;
      nodeItem.options = cartItem.options;

      nodeItem.thumbnail = cartItem.image;
      nodeItem.vatRate = cartItem.vat;
      nodeItem.url = cartItem.image;
      nodeItem.priceNet = cartItem.priceNet || (nodeItem.price / (1.0 + nodeItem.vatRate)); // Price without taxes
    }
  }

  private appendGuestTokenToRedirectUrls(payload: PaymentDetails): PaymentDetails {
    if (window.self !== window.top || !window.origin.includes(this.env.frontend.checkoutWrapper)) {
      const accessToken = this.store.selectSnapshot(AuthSelectors.accessToken);

      return produce(payload, (draft: PaymentDetails) => {
        const fields = [
          'frontendSuccessUrl',
          'frontendFinishUrl',
          'frontendFailureUrl',
          'frontendCancelUrl',
          'postbackUrl',
        ];
        fields.forEach((field) => {
          if (draft[field]?.includes(this.env.frontend.checkoutWrapper)
            || (draft[field] && window.self !== window.top)
          ) {
            const url = new URL(draft[field]);
            url.searchParams.set('guest_token', accessToken);
            draft[field] = url.toString();
          }
        });
      });
    }

    return payload;
  }

  private preparePaymentStateData(state: PaymentStateModel, paymentOptionData?: PaymentOptionDataInterface,) {
    const { paymentMethod, connectionId } = paymentOptionData ?? {};
    const flowPaymentMethod = this.store.selectSnapshot(FlowState.paymentMethod);
    const pm = paymentMethod ?? flowPaymentMethod;
    const { connectionId: flowConnectionId } = this.store.selectSnapshot(FlowState.flow);
    const { [pm]: payment } = state;
    const ci = connectionId ?? flowConnectionId;

    return {
      payment,
      connectionId: ci,
      paymentMethod: pm,
    };
  }
}
